/**
 * Social Links Normalizer
 * 
 * Aceita diferentes formatos de entrada para redes sociais:
 * - @usuario (com arroba)
 * - usuario (sem arroba)
 * - https://platform.com/usuario (URL completa)
 * - www.platform.com/usuario (URL com www)
 * - platform.com/usuario (URL sem protocolo)
 * 
 * Normaliza para URL completa com https:// e target="_blank"
 */

const SOCIAL_PLATFORMS = {
  instagram: {
    baseUrl: 'https://instagram.com',
    userPath: (user) => `${user}`,
    patterns: ['instagram.com', 'ig.me', 'insta'],
  },
  twitter: {
    baseUrl: 'https://twitter.com',
    userPath: (user) => `${user}`,
    patterns: ['twitter.com', 'x.com', 'tweet'],
  },
  linkedin: {
    baseUrl: 'https://linkedin.com',
    userPath: (user) => `in/${user}`,
    patterns: ['linkedin.com', 'linkedIn'],
  },
  youtube: {
    baseUrl: 'https://youtube.com',
    userPath: (user) => `@${user}`,
    patterns: ['youtube.com', 'youtu.be', 'yt'],
  },
  threads: {
    baseUrl: 'https://threads.net',
    userPath: (user) => `${user}`,
    patterns: ['threads.net', 'threads', 'threads.app'],
  },
  facebook: {
    baseUrl: 'https://facebook.com',
    userPath: (user) => `${user}`,
    patterns: ['facebook.com', 'fb.me', 'fb.com'],
  },
};

/**
 * Normaliza um valor de entrada para URL completa
 * 
 * @param {string} input - Valor digitado pelo usuário
 * @param {string} platform - Plataforma (instagram, twitter, etc)
 * @returns {string} URL normalizada com https://
 */
export function normalizeSocialLink(input, platform) {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // 1. Trim espacos e caracteres especiais
  const cleaned = input.trim();

  if (!cleaned) {
    return '';
  }

  const platformConfig = SOCIAL_PLATFORMS[platform];
  if (!platformConfig) {
    // Platform desconhecida, validar como URL genérica
    return normalizeGenericUrl(cleaned);
  }

  // 2. Detectar tipo de entrada
  const hasSlashes = cleaned.includes('/');
  const hasProtocol = cleaned.startsWith('http://') || cleaned.startsWith('https://');
  const hasArroba = cleaned.startsWith('@');

  // 3. Processar entrada conforme tipo

  // Se tem barras, é um link - validar protocolo
  if (hasSlashes) {
    if (hasProtocol) {
      // Já tem protocolo, apenas validar HTTPS
      const url = new URL(cleaned).href;
      return url.startsWith('https://') ? url : url.replace('http://', 'https://');
    } else {
      // Não tem protocolo, adicionar HTTPS
      return `https://${cleaned}`;
    }
  }

  // É um username (sem barras)
  let username = cleaned;

  // Se tem arroba, remover
  if (hasArroba) {
    username = cleaned.substring(1);
  }

  // Construir URL final
  const userPath = platformConfig.userPath(username);
  return `${platformConfig.baseUrl}/${userPath}`;
}

/**
 * Normaliza URLs genéricas (não-sociais)
 * 
 * @param {string} input - URL digitada
 * @returns {string} URL normalizada
 */
export function normalizeGenericUrl(input) {
  if (!input || typeof input !== 'string') {
    return '';
  }

  const cleaned = input.trim();

  if (!cleaned) {
    return '';
  }

  // Se já tem protocolo, validar HTTPS
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
    const url = new URL(cleaned).href;
    return url.startsWith('https://') ? url : url.replace('http://', 'https://');
  }

  // Se começa com www. ou tem ponto, é provavelmente URL
  if (cleaned.startsWith('www.') || cleaned.includes('.')) {
    return `https://${cleaned}`;
  }

  // Caso contrário, tentar adicionar https://
  return `https://${cleaned}`;
}

/**
 * Normaliza links genéricos (não-sociais)
 * 
 * @param {string} url - URL do link
 * @returns {string} URL normalizada
 */
export function normalizeLink(url) {
  return normalizeGenericUrl(url);
}

/**
 * Normaliza um objeto de social links completo
 * 
 * @param {Object} socialLinks - Objeto com redes sociais {instagram: {url, name}, ...}
 * @returns {Object} Objeto normalizado
 */
export function normalizeSocialLinks(socialLinks) {
  const normalized = {};

  Object.entries(socialLinks || {}).forEach(([platform, data]) => {
    if (data && data.url) {
      normalized[platform] = {
        ...data,
        url: normalizeSocialLink(data.url, platform),
      };
    } else if (data) {
      normalized[platform] = data;
    }
  });

  return normalized;
}

/**
 * Normaliza um array de links genéricos
 * 
 * @param {Array} genericLinks - Array de {url, name}
 * @returns {Array} Array normalizado
 */
export function normalizeGenericLinks(genericLinks) {
  return (genericLinks || []).map((link) => ({
    ...link,
    url: link.url ? normalizeGenericUrl(link.url) : '',
  }));
}

export default {
  normalizeSocialLink,
  normalizeGenericUrl,
  normalizeLink,
  normalizeSocialLinks,
  normalizeGenericLinks,
};
